/**
 * Reading and writing the filter bag.
 *
 * Every filter is stored as an ARRAY whatever its control — a `select` holds one element, a
 * `toggle` holds `[true]` — so that the predicate, the active count and the fetcher contract never
 * branch on filter type. These helpers are the only place that shape is interpreted, and they are
 * pure so the two places that render controls (the toolbar's inline pills and the field list behind
 * the Filters trigger) cannot read the same value differently.
 */
import type { TableFilter, TableFilterValue, TableRowKey } from './types';

type Values = Record<string, TableFilterValue[]>;

/** The value a control reads back, normalised out of the array every filter is stored as. */
export function filterValue(values: Values, filter: TableFilter): TableFilterValue[] {
  return values[filter.key] || [];
}

export function singleFilterValue(values: Values, filter: TableFilter): TableFilterValue | null {
  return filterValue(values, filter)[0] ?? null;
}

export function toggleFilterValue(values: Values, filter: TableFilter): boolean {
  return filterValue(values, filter).length > 0;
}

export function isFilterActive(values: Values, filter: TableFilter): boolean {
  return filterValue(values, filter).length > 0;
}

/** ZonePillGroup speaks { id, label }; a filter's options speak { id, name }. */
export function pillItems(filter: TableFilter): { id: TableRowKey; label: string }[] {
  return (filter.options || []).map((option) => ({ id: option.id, label: option.name }));
}

/**
 * What a single-value control (a `select`, a pill group in single mode) writes. Empty, null and
 * undefined all mean "cleared" rather than "the value is nothing".
 */
export function asSingleValue(value: unknown): TableFilterValue[] {
  return value === null || value === undefined || value === '' ? [] : [value as TableFilterValue];
}

export function asToggleValue(checked: boolean): TableFilterValue[] {
  return checked ? [true] : [];
}
